// ─────────────────────────────────────────────────────────────────────────────
// Drop this device's push registration on the way out.
//
// ⚠️ THE ROW USED TO SURVIVE LOGOUT FOREVER. usePushNotifications registers a
// device_token on every launch, DELETE /api/notifications/unregister-device has
// existed the whole time, and NOTHING in the app has ever called it. So a device
// stayed targetable by push for whoever last signed in on it — and on a handset
// that has hosted two accounts, both rows persisted and both stayed targetable,
// so the icon badge showed whichever user's count arrived last.
//
// ⚠️ FIRED, NEVER AWAITED. A logout that can fail — or that hangs on a cleanup
// call while the network is down — is worse than a stale row by a wide margin:
// one is a wrong number on an icon, the other traps a user in a session they
// have asked to end. The caller does not await this, nothing it does can throw
// into the logout path, and every failure inside it is swallowed on purpose.
// The cost of that choice is honest: a failed unregister leaves exactly the
// orphan row this function exists to remove, and the server-side prune on a
// failed delivery is what eventually collects it.
//
// ⚠️ ORDER IS LOAD-BEARING. The route is requireAuth'd, so the request must
// carry a still-valid JWT — it is passed in and captured here rather than read
// from state, because the caller clears state immediately after firing this and
// an in-flight request must not race that.
//
// ⚠️ keepalive: THE REQUEST OUTLIVES THE CONTEXT THAT STARTED IT. Logout is
// routinely followed by a navigation or reload, which cancels ordinary in-flight
// fetches — precisely the fire-and-forget failure mode that would make this look
// implemented and do nothing. Unsupported engines ignore the flag and fall back
// to a normal request, so it is safe to pass unconditionally.
// ─────────────────────────────────────────────────────────────────────────────
export function unregisterDeviceToken(FirebaseMessaging, jwt, api, fetchImpl) {
  // Returned so a test can wait on it; callers never do (fired, never awaited).
  return (async () => {
    try {
      if (!jwt) return;
      // The token for THIS install — the same value usePushNotifications
      // registered, so it names the row to delete without any shared state
      // between the two. Rejects when push was never set up; that is a no-op,
      // not an error, and there is nothing to unregister in that case.
      const { token: deviceToken } = await FirebaseMessaging.getToken();
      if (!deviceToken) return;
      await (fetchImpl || fetch)(`${api}/api/notifications/unregister-device`, {
        method: 'DELETE',
        keepalive: true,
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_token: deviceToken }),
      });
    } catch (_) {
      /* Deliberately silent — see the non-blocking note above. */
    }
  })();
}

